import type { TableProps } from "@amzn/awsui-components-console";

import { SORTING_COMPARATORS } from "./derivations/comparators";
import { deriveDisplayValue } from "./derivations/valueDerivations";
import type { ClaimRecord, ColumnDefinition, SearchConfig } from "./types";

/** A Cloudscape table column over the loose claim record the service returns. */
export type ClaimColumnDefinition = TableProps.ColumnDefinition<ClaimRecord>;

/**
 * The sort label the legacy per-line-of-business tables produced, character for
 * character, so the migration did not change what a screen reader announces.
 *
 * Returns undefined when the column offers no ordering, so a non-sortable
 * header is not announced as "not sorted".
 */
function createSortLabelFn(
  header: string,
  sorting: TableProps.SortingColumn<ClaimRecord>
): ClaimColumnDefinition["ariaLabel"] {
  if (!sorting.sortingField && !sorting.sortingComparator) {
    return undefined;
  }
  return ({ sorted, descending }) =>
    `${header}, ${sorted ? `sorted ${descending ? "descending" : "ascending"}` : "not sorted"}.`;
}

/**
 * A declared column's ordering as Cloudscape expects it: either a raw field
 * name or a comparator, never both.
 *
 * A column declaring a field *and* a derivation gets a comparator over the
 * derived display strings rather than the field, because a raw
 * `sortingField` would order rows by the underlying property and so sort by a
 * value the user cannot see. `useCollection` ascends on the first header
 * activation and descends on the second, and its sort is stable, so equal
 * derived values hold their existing relative order without further work.
 */
function buildSorting(column: ColumnDefinition): TableProps.SortingColumn<ClaimRecord> {
  if (column.sorting.kind === "comparator") {
    return { sortingComparator: SORTING_COMPARATORS[column.sorting.comparator](column) };
  }
  if (column.derivation) {
    return { sortingComparator: SORTING_COMPARATORS.derivedDisplayValue(column) };
  }
  return { sortingField: column.sorting.sortingField };
}

/**
 * Exactly one Cloudscape column per declared Column_Definition, in declared
 * order, with nothing appended. The declared array is what is iterated, so the
 * emitted order is the declared order and a property arriving on a claim that
 * no Column_Definition names cannot become a column.
 */
export function buildColumnDefinitions(config: SearchConfig): ClaimColumnDefinition[] {
  return config.table.columns.map((column) => {
    const sorting = buildSorting(column);
    return {
      id: column.columnId,
      header: column.header,
      cell: (claim: ClaimRecord) => deriveDisplayValue(column, claim),
      ...(column.width === undefined ? {} : { width: column.width }),
      ...sorting,
      ariaLabel: createSortLabelFn(column.header, sorting),
    };
  });
}
